import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Shield,
  CircleNotch,
  CheckCircle,
  XCircle,
  Warning,
  Envelope,
  Lightning,
  Prohibit,
  ChartBar,
  Gear,
  Trash,
  PaperPlaneTilt,
} from '@phosphor-icons/react';


function StatCard({ icon: Icon, label, value, color = 'blue', subtext }) {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-emerald-500 to-emerald-600',
    yellow: 'from-amber-500 to-amber-600',
    red: 'from-red-500 to-red-600',
    violet: 'from-violet-500 to-fuchsia-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colors[color]} text-white flex items-center justify-center`}>
          <Icon size={20} weight="fill" />
        </div>
      </div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
      {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
    </div>
  );
}


export default function OutreachSafetyPage() {
  const [stats, setStats] = useState(null);
  const [warmupCfg, setWarmupCfg] = useState({ start_quota: 20, daily_increment: 10, max_quota: 200 });
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [dnc, setDnc] = useState([]);
  const [newDncEmail, setNewDncEmail] = useState('');

  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    try {
      setLoading(true);
      const [s, d] = await Promise.all([
        axios.get(`${API_URL}/api/outreach/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/outreach/dnc`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setDnc(d.data?.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const validateAllProspects = async () => {
    if (!window.confirm('¿Validar TODOS los emails de prospectos contra DNS/MX? Puede tardar varios minutos.')) return;
    setValidating(true);
    try {
      const res = await axios.post(`${API_URL}/api/outreach/validate-prospects`, {}, { withCredentials: true, timeout: 300000 });
      alert(`Resultado: ${res.data.valid} válidos · ${res.data.invalid} inválidos · ${res.data.unknown} desconocidos`);
      refresh();
    } catch (e) {
      alert('Error: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setValidating(false);
    }
  };

  const testValidate = async () => {
    if (!testEmail) return;
    try {
      const res = await axios.post(`${API_URL}/api/outreach/validate-email`, { email: testEmail }, { withCredentials: true });
      setTestResult(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const saveWarmupConfig = async () => {
    setSavingCfg(true);
    try {
      await axios.post(`${API_URL}/api/outreach/warmup/config`, warmupCfg, { withCredentials: true });
      alert('Configuración guardada');
      refresh();
    } catch (e) {
      alert('Error: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingCfg(false);
    }
  };

  const resetWarmup = async () => {
    if (!window.confirm('¿Reiniciar contador del warm-up? Volverá a empezar desde el quota inicial.')) return;
    try {
      await axios.post(`${API_URL}/api/outreach/warmup/config`, { reset_started_at: true }, { withCredentials: true });
      alert('Warm-up reiniciado');
      refresh();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const addDnc = async () => {
    if (!newDncEmail) return;
    try {
      await axios.post(`${API_URL}/api/outreach/dnc`, { email: newDncEmail, reason: 'Manual add' }, { withCredentials: true });
      setNewDncEmail('');
      refresh();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const removeDnc = async (email) => {
    if (!window.confirm(`¿Quitar ${email} de la lista DNC?`)) return;
    try {
      await axios.delete(`${API_URL}/api/outreach/dnc/${encodeURIComponent(email)}`, { withCredentials: true });
      refresh();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircleNotch size={40} className="animate-spin text-violet-600" />
      </div>
    );
  }

  const q = stats?.warmup_quota || {};
  const ev = stats?.email_validation || {};
  const usagePercent = q.max ? Math.min(100, Math.round((q.used / q.max) * 100)) : 0;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="outreach-safety-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center">
          <Shield size={22} weight="fill" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Seguridad de Outreach</h1>
          <p className="text-sm text-slate-500">Validador, Warm-up, Reply Classifier y DNC — protege tu dominio</p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={PaperPlaneTilt} label="Enviados hoy" value={stats?.sent_today || 0} color="blue" subtext={`Quota: ${q.max || 0}`} />
        <StatCard icon={Lightning} label="Disponible hoy" value={q.available || 0} color="violet" subtext={`Día ${q.days_elapsed || 0} de warm-up`} />
        <StatCard icon={Envelope} label="Respuestas 7d" value={stats?.replies_last_7_days || 0} color="green" subtext={`${stats?.interested_last_7_days || 0} interesados`} />
        <StatCard icon={Prohibit} label="DNC list" value={stats?.do_not_contact_total || 0} color="red" subtext="No contactar" />
      </div>

      {/* Warm-up Progress Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Lightning size={20} weight="fill" className="text-amber-500" />
              Warm-up de Dominio
            </h2>
            <p className="text-sm text-slate-500 mt-1">Escalado gradual para evitar blacklist (recomendado por Resend, Gmail, Outlook)</p>
          </div>
          <button
            onClick={resetWarmup}
            className="text-xs text-slate-500 hover:text-slate-700"
            data-testid="reset-warmup-btn"
          >
            Reiniciar
          </button>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-semibold text-slate-700">{q.used || 0} / {q.max || 0} emails</span>
            <span className="text-slate-500">Día {q.days_elapsed || 0} · cap máximo: {q.max_quota_cap || 200}/día</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${usagePercent >= 90 ? 'bg-red-500' : usagePercent >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${usagePercent}%` }}
            />
          </div>
        </div>

        {/* Config */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-slate-100">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Quota inicial (día 1)</label>
            <input
              type="number"
              min={1}
              value={warmupCfg.start_quota}
              onChange={(e) => setWarmupCfg({ ...warmupCfg, start_quota: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
              data-testid="warmup-start-input"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Incremento diario</label>
            <input
              type="number"
              min={0}
              value={warmupCfg.daily_increment}
              onChange={(e) => setWarmupCfg({ ...warmupCfg, daily_increment: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
              data-testid="warmup-increment-input"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Tope máximo (cap)</label>
            <input
              type="number"
              min={10}
              value={warmupCfg.max_quota}
              onChange={(e) => setWarmupCfg({ ...warmupCfg, max_quota: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
              data-testid="warmup-max-input"
            />
          </div>
        </div>
        <button
          onClick={saveWarmupConfig}
          disabled={savingCfg}
          className="mt-3 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 disabled:opacity-50"
          data-testid="save-warmup-btn"
        >
          {savingCfg ? 'Guardando...' : 'Guardar Config'}
        </button>
      </div>

      {/* Email Validation */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-emerald-500" />
              Validación de Emails
            </h2>
            <p className="text-sm text-slate-500 mt-1">Verifica MX/DNS gratis · evita bounces masivos</p>
          </div>
          <button
            onClick={validateAllProspects}
            disabled={validating}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
            data-testid="validate-all-btn"
          >
            {validating ? <CircleNotch size={16} className="animate-spin" /> : <Lightning size={16} weight="fill" />}
            {validating ? 'Validando...' : 'Validar todos los Prospectos'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-emerald-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-emerald-700">{ev.VALID || 0}</div>
            <div className="text-xs text-emerald-600 uppercase font-semibold">Valid</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-700">{ev.INVALID || 0}</div>
            <div className="text-xs text-red-600 uppercase font-semibold">Invalid</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-amber-700">{ev.UNKNOWN || 0}</div>
            <div className="text-xs text-amber-600 uppercase font-semibold">Unknown</div>
          </div>
        </div>

        {/* Test single email */}
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs font-semibold text-slate-600 mb-2">Probar un email individual:</p>
          <div className="flex gap-2">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="ejemplo@empresa.com"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              data-testid="test-email-input"
            />
            <button
              onClick={testValidate}
              className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm font-semibold hover:bg-slate-800"
              data-testid="test-email-btn"
            >
              Validar
            </button>
          </div>
          {testResult && (
            <div className={`mt-3 p-3 rounded-lg border ${
              testResult.status === 'VALID' ? 'bg-emerald-50 border-emerald-200' :
              testResult.status === 'INVALID' ? 'bg-red-50 border-red-200' :
              'bg-amber-50 border-amber-200'
            }`} data-testid="test-result">
              <div className="flex items-center gap-2 font-bold">
                {testResult.status === 'VALID' ? <CheckCircle size={18} className="text-emerald-600" weight="fill" /> :
                 testResult.status === 'INVALID' ? <XCircle size={18} className="text-red-600" weight="fill" /> :
                 <Warning size={18} className="text-amber-600" weight="fill" />}
                {testResult.status} · risk: {testResult.risk}
              </div>
              <ul className="text-xs mt-1 ml-6 list-disc">
                {testResult.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Do Not Contact */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-4">
          <Prohibit size={20} weight="fill" className="text-red-500" />
          Lista DNC (Do Not Contact)
        </h2>
        <p className="text-sm text-slate-500 mb-4">Emails que NO recibirán más outreach. Se llena automáticamente cuando un prospect responde "unsubscribe".</p>

        <div className="flex gap-2 mb-4">
          <input
            type="email"
            value={newDncEmail}
            onChange={(e) => setNewDncEmail(e.target.value)}
            placeholder="email@bloquear.com"
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
            data-testid="dnc-input"
          />
          <button
            onClick={addDnc}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700"
            data-testid="add-dnc-btn"
          >
            Bloquear
          </button>
        </div>

        {dnc.length === 0 ? (
          <p className="text-center text-slate-400 text-sm py-6">No hay emails bloqueados</p>
        ) : (
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {dnc.map((d, i) => (
              <div key={i} className="flex items-center justify-between py-2 text-sm" data-testid={`dnc-row-${i}`}>
                <div>
                  <p className="font-medium text-slate-800">{d.email}</p>
                  <p className="text-xs text-slate-400">{d.reason} · {d.added_at?.split('T')[0]}</p>
                </div>
                <button
                  onClick={() => removeDnc(d.email)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                  data-testid={`remove-dnc-${i}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DNS Setup Guide */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <h2 className="text-base font-bold text-blue-900 flex items-center gap-2 mb-3">
          <Gear size={18} weight="fill" />
          🟢 Recomendación DNS: Subdominio dedicado para outreach
        </h2>
        <p className="text-sm text-blue-800 mb-3">
          Para proteger tu dominio principal (<code className="bg-white px-1 rounded">iqeslowvoltage.com</code>) de quedar marcado como spam, configura un subdominio dedicado para cold outreach:
        </p>
        <ol className="text-sm text-blue-900 space-y-1 ml-5 list-decimal">
          <li>En Resend, agrega un nuevo dominio: <code className="bg-white px-1 rounded">outbound.iqeslowvoltage.com</code></li>
          <li>Resend te da 4 registros DNS (TXT/MX). Agrégalos en tu proveedor DNS (GoDaddy, Cloudflare, etc.).</li>
          <li>Espera 30-60 min a que Resend verifique.</li>
          <li>En el CRM, cambia <code className="bg-white px-1 rounded">SENDER_EMAIL</code> a <code className="bg-white px-1 rounded">outreach@outbound.iqeslowvoltage.com</code></li>
        </ol>
        <p className="text-xs text-blue-700 mt-3 italic">
          Si tu dominio principal queda marcado, las facturas a clientes reales también caen en spam. Por eso este paso es crítico antes de lanzar a 2,000 prospectos.
        </p>
      </div>
    </div>
  );
}
